import { FC, useMemo, ReactNode, useCallback } from 'react'
import * as groupBy from 'lodash/groupBy'
import styles from 'components/features/pfm/Navigation/Navigation.styl'
import cn from 'classnames'
import { MENU_NAV_PAGE_DEFINITIONS, COACH_PAGE } from 'constants/pages'
import { MenuNavPageType } from 'interfaces/pages'
import { IS_STATUS_APP_IOS, IS_STATUS_APP } from 'helpers/userAgentHelpers'
import { Prompt } from 'react-router'
import { NavigationItemProps, NavigationItem } from 'components/features/pfm/Navigation/NavigationItem'
import { useCurrentUrl } from 'helpers/hookHelpers'
import SvgIcon from 'components/atoms/SvgIcon'
import LogoutIcon from 'icons/logout.svg'
import { NavigationLink } from 'components/atoms/navigation/NavigationLink'
import { logOutActionCreator } from 'actions/authActions'
import { useStoreDispatch, useStoreSelector } from 'helpers/reactReduxHelpers'
import { EmailLink } from 'components/features/pfm/EmailLink/EmailLink'
import { SUPPORT_EMAIL } from 'constants/email'
import { NavigationRenderMode } from 'interfaces/navigation'
import { LOGOUT_URL } from 'tangle-shared'
import { setShownSidebarPaneActionCreator } from 'actions/sidebarActions'
import { FeatureFlag, FeatureFlags } from 'interfaces/global'
import { featureFlagsSelector } from 'selectors/globalSelectors'
import { featureFlagsGuard } from 'guards/globalGuards'
import { useSimpleGuard } from 'helpers/loadableHelpers'

const PREVENT_NAVIGATION = IS_STATUS_APP_IOS

type NavigationItemType = {
  name: MenuNavPageType
  items?: NavigationItemType[]
  excludeCompact?: boolean
  requiredFlag?: FeatureFlag
}

const MENU_STRUCTURE: NavigationItemType[] = [
  { name: 'dashboard',
    items: [
      { name: 'linkedaccounts' },
      { name: 'transactions' },
      { name: 'spending' },
      { name: 'income' },
      { name: 'debts' },
      { name: 'assets' },
      { name: 'networth' },
      { name: 'creditscore' },
    ] },
  { name: 'feedposts' },
  { name: COACH_PAGE, requiredFlag: 'enable_coaching' },
  { name: 'creditcard' },
  { name: 'future' },
  { name: 'opportunities', excludeCompact: true },
  { name: 'rewards', excludeCompact: true },
  { name: 'settings', excludeCompact: true },
]

const groupedMenuNavPageDefinitions = groupBy(MENU_NAV_PAGE_DEFINITIONS, 'name')

const createGetNavigationItems = (currentUrl: string, mode: NavigationRenderMode, memberFlags: FeatureFlags) => {
  const isCompact = mode === 'compact'

  const getNavigationItems = (items: NavigationItemType[], nested = false): NavigationItemProps[] => {
    return items.reduce<NavigationItemProps[]>(
      (acc, { name, items: childItems, excludeCompact, requiredFlag }) => {
        const foundPage = groupedMenuNavPageDefinitions[name][0]

        const isAvailable = !requiredFlag || (!!memberFlags && memberFlags.includes(requiredFlag))
        if (!foundPage || (isCompact && excludeCompact) || !isAvailable) {
          return acc
        }

        const { title, url } = foundPage

        acc.push({
          name,
          title,
          url,
          isNested: nested,
          isActive: currentUrl === url,
          mode,
        })

        if (!isCompact && childItems && childItems.length) {
          acc.push(...getNavigationItems(childItems, true))
        }

        return acc
      },
      []
    )
  }

  return getNavigationItems
}

export type NavigationProps = {
  mode: NavigationRenderMode
}

export const Navigation: FC<NavigationProps> = (props) => {
  const { mode } = props
  useSimpleGuard(featureFlagsGuard, props, 'Main Navigation')

  const memberFlags = useStoreSelector(featureFlagsSelector)
  const dispatch = useStoreDispatch()
  const currentPageUrl = useCurrentUrl()

  const isCompact = mode === 'compact'

  const itemsWrapperChild = useMemo<ReactNode>(() => {
    const itemsChildren = createGetNavigationItems(currentPageUrl, mode, memberFlags || [])(MENU_STRUCTURE)
      .map((item) => (
        <div key={ item.name } className={ cn(styles.itemWrapper, isCompact && styles.withCompact) }>
          <NavigationItem { ...item } mode={ mode } />
        </div>
      ))

    return (
      <div className={ styles.itemsWrapper }>
        { itemsChildren }
      </div>
    )
  }, [currentPageUrl, mode, isCompact, memberFlags])

  const logoutButton = useMemo<ReactNode>(() => {
    const link = (
      <>
        <div className={ styles.icon }>
          <SvgIcon src={ LogoutIcon } />
        </div>
        <span className={ styles.text }>
          Logout
        </span>
      </>
    )

    if (IS_STATUS_APP) {
      return (
        <a
          className={ styles.button }
          onClick={ () => dispatch(logOutActionCreator({ to: LOGOUT_URL, redirect: false })) }
        >
          { link }
        </a>
      )
    }

    return (
      <NavigationLink
        className={ styles.button }
        to='logout'
        preventNavigation={ PREVENT_NAVIGATION }
        onClick={ () => dispatch(logOutActionCreator()) }
      >
        { link }
      </NavigationLink>
    )
  }, [dispatch])

  const helpButton = useMemo<ReactNode>(() => {
    return (
      <EmailLink className={ styles.button } email={ SUPPORT_EMAIL }>
        Need Help?
      </EmailLink>
    )
  }, [])

  const rootClassName = cn(styles.navigation, {
    [styles.compact]: isCompact,
  })

  const handleClick = useCallback(() => {
    dispatch(setShownSidebarPaneActionCreator(null))
  }, [dispatch])

  return (
    <section className={ rootClassName } onClick={ handleClick }>
      { itemsWrapperChild }
      { !isCompact && (
        <div className={ styles.buttonButtonsWrapper }>
          { logoutButton }
          { helpButton }
        </div>
      )}
      <Prompt
        key='block nav to capture the correct shot after closing the menu'
        when={ PREVENT_NAVIGATION }
        message=''
      />
    </section>
  )
}
